import { useState } from "react";
import { useSearchParams } from "react-router";
import { Button, Dialog, FormRow, FormSection, ReadOnlyNote, formatDate } from "@omnabase/ui";
import { WORKSPACE_DELETION_GRACE_DAYS, memberName, type Workspace } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useSetParam } from "../../lib/nav";
import { useSession } from "../../shell/store";

/** Settings › Legal & data (2026-10-20; DECISIONS "2026-10-20 — Logos, Legal & data…"): General's Legal and Data sections on their own page.
 *  Everyone reads which terms and DPA (data processing agreement) apply; the DPA's download, "Export everything" and "Delete workspace…"
 *  (with its dialog at `?delete=1`) are the owner's, as the API's rule (the red team's item 10: General showed them to Administrators too). */
export function LegalSettings() {
  const { workspace } = useSession();
  const owner = workspace.me.role === "owner";
  const [params] = useSearchParams(); const setParam = useSetParam();
  return (
    <>
      <div className="ob-page__head"><div><h1 className="ob-page__title">Legal &amp; data</h1><p className="ob-page__subtitle">The terms and the data processing agreement in force, and the workspace's data: an export of everything, or its deletion.</p></div></div>
      {!owner && <ReadOnlyNote>the owner downloads, exports and deletes.</ReadOnlyNote>}
      <div className="ob-form">
        <LegalSection workspace={workspace} owner={owner} />
        {owner ? <DataSection /> : <FormSection title="Data" intro="Everything the workspace holds can leave as one archive, or be deleted. Only the owner does either.">{null}</FormSection>}
      </div>
      {owner && params.get("delete") === "1" && <DeleteWorkspaceDialog onClose={() => setParam("delete", null)} />}
    </>
  );
}

function termsLine(w: Workspace): string {
  if (!w.terms.version) return `Nothing accepted yet (the version in force is ${w.terms.current}).`;
  const by = `Version ${w.terms.version}, accepted by ${memberName(w.members, w.terms.acceptedBy)}${w.terms.acceptedAt ? ` on ${formatDate(w.terms.acceptedAt)}` : ""}.`;
  return w.terms.current !== w.terms.version && w.terms.current !== "0" ? `${by} Version ${w.terms.current} is in force: the owner accepts it on the dashboard.` : by;
}
function dpaLine(w: Workspace): string {
  if (!w.dpa.version) return `Nothing accepted yet (the version in force is ${w.dpa.current}).`;
  return `Version ${w.dpa.version}${w.dpa.acceptedAt ? `, accepted on ${formatDate(w.dpa.acceptedAt)}` : ""}. The version in force is ${w.dpa.current}.`;
}

function LegalSection({ workspace: w, owner }: { workspace: Workspace; owner: boolean }) {
  return (
    <FormSection title="Legal" intro="The terms of service and the data processing agreement (DPA) the workspace accepted, and the versions in force.">
      <FormRow label="Terms of service" help={termsLine(w)}><a className="ob-link" href="/legal/terms" target="_blank" rel="noopener noreferrer">Read the terms</a></FormRow>
      <FormRow label="Data processing agreement" help={dpaLine(w)}>{owner ? <a className="ob-link" href={`/api/v1/w/${w.slug}/dpa`} download>Download the DPA (data processing agreement)</a> : <a className="ob-link" href="/legal/dpa" target="_blank" rel="noopener noreferrer">Read the DPA</a>}</FormRow>
    </FormSection>
  );
}

function DataSection() {
  const { workspace, toast } = useSession(); const setParam = useSetParam();
  const exportWorkspace = async () => { try { await api.post(`/api/v1/w/${workspace.slug}/export`, {}); toast("Export started · you get a mail with the download", { kind: "success" }); } catch (e) { toast((e as Error).message, { kind: "error" }); } };
  return (
    <FormSection title="Data" intro="Export everything the workspace holds, or delete it.">
      <FormRow label="Export" help="Tickets, customers, knowledge base and team chat as one archive. We email the owner a link when it is ready."><Button size="sm" icon="download" onClick={() => void exportWorkspace()}>Export everything</Button></FormRow>
      <FormRow label="Delete workspace" help={`Owner only. Removes every project, ticket and customer after a ${WORKSPACE_DELETION_GRACE_DAYS}-day grace period.`}><Button size="sm" onClick={() => setParam("delete", "1")}>Delete workspace…</Button></FormRow>
    </FormSection>
  );
}

/** The workspace's deletion (M6 round two): the typed address arms it; the grace period's sentence; every member then sees the banner with the date, the owner can keep it */
function DeleteWorkspaceDialog({ onClose }: { onClose: () => void }) {
  const { workspace, reload, toast } = useSession();
  const [confirm, setConfirm] = useState(""); const [busy, setBusy] = useState(false);
  const armed = confirm.trim().toLowerCase() === workspace.slug;
  const submit = async () => {
    if (!armed || busy) return; setBusy(true);
    try { await api.del(`/api/v1/w/${workspace.slug}`, { confirm: confirm.trim() }); toast(`Deletion requested · the workspace is deleted in ${WORKSPACE_DELETION_GRACE_DAYS} days unless you keep it`, { kind: "success" }); onClose(); await reload(); }
    catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title={`Delete ${workspace.name}?`} onClose={onClose} intro={`Every project, ticket, customer and file goes in ${WORKSPACE_DELETION_GRACE_DAYS} days. Until then every member sees the date and you can keep it. The address ${workspace.slug} is never given out again.`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!armed || busy} onClick={() => void submit()}>Delete workspace</Button></>}>
      <div className="ob-dialog__field"><span className="ob-dialog__label">Type the workspace's address to confirm</span><input className="ob-dialog__input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={workspace.slug} aria-label="Workspace address" autoFocus /></div>
    </Dialog>
  );
}
